import React, { ChangeEvent, FormEvent, useRef, useState } from 'react';
import PageHeader from '../tenant_portal/shared/PageHeader';
import PortalButton from '../components/PortalButton';
import loginStyles from '../../../stylesheets/components/login.module.scss';
import signupAgreementStyles from '../../../stylesheets/components/loginAgreement.module.scss';
import DocumentLink from '../components/legal_documents/DocumentLink';

const AGREEMENT_VALIDATION_MESSAGE =
  'Cannot continue until the terms of service and privacy policy have been agreed to.';

const initialSignupAgreementData = LOGIN_PORTAL_DATA.signupAgreement;

const SignupAgreement = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const hasExistingAgreement =
    (initialSignupAgreementData?.agreedOn || '') !== '';
  const [isChecked, setIsChecked] = useState(hasExistingAgreement);
  const [errorMessage, setErrorMessage] = useState(
    initialSignupAgreementData?.errorMessage || ''
  );

  const agreedOnValue = isChecked
    ? initialSignupAgreementData?.agreedOn || new Date().toISOString()
    : '';

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextChecked = event.target.checked;

    setIsChecked(nextChecked);

    if (nextChecked) {
      setErrorMessage('');
    }
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (isChecked || hasExistingAgreement) {
      return;
    }

    event.preventDefault();
    setErrorMessage(AGREEMENT_VALIDATION_MESSAGE);
  };

  const handleContinueClick = () => {
    formRef.current?.requestSubmit();
  };

  return (
    <div
      className={`${loginStyles.loginRouteContent} ${signupAgreementStyles.routeContent}`}
    >
      <PageHeader title="Terms & Privacy Policy" includeBottomMargin={false} />
      <div className={signupAgreementStyles.error} aria-live="polite">
        {errorMessage}
      </div>
      <form
        ref={formRef}
        action="/sign_agreement"
        className={signupAgreementStyles.form}
        id="signup-agreement-form"
        method="post"
        onSubmit={handleFormSubmit}
      >
        <input name="authenticity_token" type="hidden" value={AUTH_TOKEN} />
        <input name="_method" type="hidden" value="patch" />
        <input
          name="login[agreement_relationships_attributes][0][agreement_attributes][type]"
          type="hidden"
          value="TermsAndConditionsAgreement"
        />
        <input
          name="login[agreement_relationships_attributes][0][agreement_attributes][agreed_on]"
          readOnly
          type="hidden"
          value={agreedOnValue}
        />

        <div
          className={signupAgreementStyles.documents}
          role="list"
          aria-label="Agreement documents"
        >
          <div className={signupAgreementStyles.documentItem} role="listitem">
            <DocumentLink
              className={signupAgreementStyles.document}
              document="terms"
            >
              <span className={signupAgreementStyles.documentLabel}>
                Terms of Service
              </span>
              <span
                aria-hidden="true"
                className={signupAgreementStyles.documentChevron}
              />
            </DocumentLink>
          </div>
          <div className={signupAgreementStyles.documentItem} role="listitem">
            <DocumentLink
              className={signupAgreementStyles.document}
              document="policy"
            >
              <span className={signupAgreementStyles.documentLabel}>
                Privacy Policy
              </span>
              <span
                aria-hidden="true"
                className={signupAgreementStyles.documentChevron}
              />
            </DocumentLink>
          </div>
        </div>

        <label className={signupAgreementStyles.checkboxRow} htmlFor="i_agree">
          <input
            checked={isChecked}
            className={signupAgreementStyles.checkbox}
            id="i_agree"
            name="i_agree"
            onChange={handleCheckboxChange}
            type="checkbox"
            value="1"
          />
          <span className={signupAgreementStyles.checkboxLabel}>
            I agree to the Terms of Service and Privacy Policy.
          </span>
        </label>
        <PortalButton
          dataTestClass="signup-agreement-continue-button"
          extraClasses={[
            loginStyles.loginPrimaryButton,
            signupAgreementStyles.submitButton,
          ]}
          onClick={handleContinueClick}
        >
          Continue
        </PortalButton>
        <button hidden type="submit" />
      </form>
    </div>
  );
};

export default SignupAgreement;
